import React, { useEffect, useRef, useState } from 'react';
import type { Venue } from '@/types/venue';
import { IconMapPin as MapPin, IconAlertCircle as AlertCircle } from '@tabler/icons-react';

interface VenueMapProps {
    venues: Venue[];
    selectedId?: string | null;
}

declare global {
    interface Window {
        google: any;
        initMap: () => void;
    }
}

const VenueMap: React.FC<VenueMapProps> = ({ venues, selectedId = null }) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);
    const markersRef = useRef<any[]>([]);
    const markerByIdRef = useRef<Record<string, any>>({});
    const selectedVenue = venues.find(v => v._id === selectedId) || null;
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const initMap = () => {
            if (!mapRef.current || !window.google) return;

            try {
                // Default center (can be adjusted based on venues)
                const center = venues.length > 0 && venues[0].lat && venues[0].lng
                    ? { lat: parseFloat(venues[0].lat), lng: parseFloat(venues[0].lng) }
                    : { lat: 40.7128, lng: -74.0060 }; // New York City

                mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
                    zoom: venues.length > 1 ? 10 : 15,
                    center,
                    mapTypeId: 'roadmap',
                    styles: [
                        {
                            featureType: 'poi',
                            elementType: 'labels',
                            stylers: [{ visibility: 'off' }],
                        },
                    ],
                });

                // Clear existing markers
                markersRef.current.forEach(marker => marker.setMap(null));
                markersRef.current = [];
                markerByIdRef.current = {};

                // Add markers for each venue
                venues.forEach((venue) => {
                    if (!venue.lat || !venue.lng) return; // Skip venues without coordinates

                    // Validate coordinates
                    const lat = parseFloat(venue.lat);
                    const lng = parseFloat(venue.lng);

                    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                        console.warn(`Invalid coordinates for venue ${venue.name}: ${venue.lat}, ${venue.lng}`);
                        return;
                    }

                    const marker = new window.google.maps.Marker({
                        position: { lat, lng },
                        map: mapInstanceRef.current,
                        title: venue.name,
                        icon: {
                            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#008C95"/>
              </svg>
            `),
                            scaledSize: new window.google.maps.Size(28, 28),
                        },
                    });

                    const infoWindow = new window.google.maps.InfoWindow({
                        content: `
            <div style="padding: 4px; font-family: inherit;">
              <h3 style="font-weight: 600; color: #222; margin: 0 0 4px;">${venue.name}</h3>
              <p style="font-size: 13px; color: #888; margin: 0;">${venue.address.fullAddress}</p>
              <p style="font-size: 13px; color: #888; margin: 2px 0 0;">${venue.address.city}, ${venue.address.state} ${venue.address.zipcode}</p>
              ${venue.seatMap ? `<p style="font-size: 13px; color: #008C95; margin: 4px 0 0;">🎫 Seatmap available</p>` : ''}
            </div>
          `,
                    });

                    marker.addListener('click', () => {
                        infoWindow.open(mapInstanceRef.current, marker);
                    });

                    markersRef.current.push(marker);
                    markerByIdRef.current[venue._id] = marker;
                });

                // Fit map to show all markers
                const venuesWithCoordinates = venues.filter(venue => {
                    if (!venue.lat || !venue.lng) return false;
                    const lat = parseFloat(venue.lat);
                    const lng = parseFloat(venue.lng);
                    return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
                });

                if (venuesWithCoordinates.length > 1) {
                    const bounds = new window.google.maps.LatLngBounds();
                    venuesWithCoordinates.forEach(venue => {
                        bounds.extend({ lat: parseFloat(venue.lat!), lng: parseFloat(venue.lng!) });
                    });
                    mapInstanceRef.current.fitBounds(bounds);
                }

                setIsLoading(false);
                setError(null);
            } catch (err) {
                console.error('Error initializing map:', err);
                setError('Failed to load map. Please check your internet connection.');
                setIsLoading(false);
            }
        };

        // Load Google Maps API if not already loaded
        if (!window.google) {
            // Use Vite environment variables
            const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

            if (!apiKey) {
                setError('Google Maps API key not configured. Please add VITE_GOOGLE_MAPS_API_KEY to your .env file.');
                setIsLoading(false);
                return;
            }

            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = initMap;
            script.onerror = () => {
                setError('Failed to load Google Maps API. Please check your internet connection.');
                setIsLoading(false);
            };
            document.head.appendChild(script);
        } else {
            initMap();
        }

        // Cleanup
        return () => {
            markersRef.current.forEach(marker => marker.setMap(null));
        };
    }, [venues]);

    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map || !window.google) return;
        const marker = selectedId ? markerByIdRef.current[selectedId] : null;
        if (marker) {
            map.panTo(marker.getPosition());
            map.setZoom(15);
            return;
        }
        const located = Object.values(markerByIdRef.current);
        if (located.length > 1) {
            const bounds = new window.google.maps.LatLngBounds();
            located.forEach((m: any) => bounds.extend(m.getPosition()));
            map.fitBounds(bounds);
        }
    }, [selectedId, venues, isLoading]);

    return (
        <div className="isolate rounded-[28px] bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10 overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-5 h-14">
                <div className={`flex min-w-0 items-center gap-2 text-sm font-semibold ${selectedVenue ? 'text-primary dark:text-nightAccent' : 'text-grey10 dark:text-white'}`}>
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{selectedVenue ? selectedVenue.name : 'Venue Locations'}</span>
                </div>
                {!selectedVenue && (
                    <span className="shrink-0 rounded-full bg-grey99 px-3 py-1 text-xs font-semibold text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">
                        <span className="font-bold text-grey10 dark:text-white">{venues.length}</span> {venues.length === 1 ? 'Venue' : 'Venues'}
                    </span>
                )}
            </div>

            {error ? (
                <div className="h-96 flex items-center justify-center">
                    <div className="text-center px-6">
                        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                        <p className="text-red-600 dark:text-red-400 font-medium text-sm">Map error</p>
                        <p className="text-grey40 dark:text-nightMuted text-xs mt-1">{error}</p>
                    </div>
                </div>
            ) : venues.length === 0 ? (
                <div className="h-96 mx-3 mb-3 rounded-[20px] bg-grey99 dark:bg-white/[0.04] flex items-center justify-center">
                    <div className="text-center px-6">
                        <span className="w-12 h-12 rounded-full bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10 text-grey60 dark:text-nightMuted flex items-center justify-center mx-auto mb-3">
                            <MapPin className="w-6 h-6" />
                        </span>
                        <p className="text-grey40 dark:text-nightMuted text-sm">No venues to display on the map</p>
                    </div>
                </div>
            ) : (
                <div className="relative mx-3 mb-3 overflow-hidden rounded-[20px]">
                    {isLoading && (
                        <div className="absolute inset-0 bg-white/75 dark:bg-nightBg/75 flex items-center justify-center z-10">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary dark:border-nightAccent mx-auto mb-2"></div>
                                <p className="text-sm font-semibold text-grey40 dark:text-nightMuted">Loading map...</p>
                            </div>
                        </div>
                    )}
                    <div
                        ref={mapRef}
                        className="h-64 w-full lg:h-[464px]"
                        role="img"
                        aria-label="Map showing venue locations"
                    />
                </div>
            )}
        </div>
    );
};

export default VenueMap;
